import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../config/firebase";

export const useChatData = (chatId) => {
  const [chatData, setChatData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!chatId) return;

    const fetchChat = async () => {
      try {
        console.log('🔍 useChatData - Fetching chat with ID:', chatId);
        const ref = doc(db, "chats", chatId);
        const snap = await getDoc(ref);
        
        if (snap.exists()) {
          const data = snap.data();
          console.log('✅ useChatData - Chat data fetched:', data);
          
          // Try to fetch lawyer details from users collection if not available in chat
          let enhancedData = { ...data };
          
          // Always try to fetch lawyer details if lawyerId exists
          if (data.lawyerId) {
            try {
              console.log('🔍 useChatData - Fetching lawyer details from lawyers collection for ID:', data.lawyerId);
              const lawyerRef = doc(db, "lawyers", data.lawyerId);
              const lawyerSnap = await getDoc(lawyerRef);
              
              if (lawyerSnap.exists()) {
                const lawyerData = lawyerSnap.data();
                console.log('✅ useChatData - Lawyer data fetched from lawyers collection:', lawyerData);
                
                // Enhance chat data with lawyer information
                enhancedData = {
                  ...data,
                  lawyerName: data.lawyerName || lawyerData.full_name || lawyerData.fullName || lawyerData.name || lawyerData.displayName || 'Lawyer',
                  lawyerProfilePic: data.lawyerProfilePic || data.lawyerPhoto || lawyerData.profile_picture || lawyerData.profilePic || lawyerData.profilePicUrl || lawyerData.photoURL || '/default-lawyer.png',
                  specialization: data.specialization || lawyerData.specialization || lawyerData.practiceAreas?.join(', ') || "General Law"
                };
              } else {
                // Try users collection as fallback
                console.log('🔍 useChatData - Lawyer not found in lawyers collection, trying users collection for ID:', data.lawyerId);
                const userRef = doc(db, "users", data.lawyerId);
                const userSnap = await getDoc(userRef);
                
                if (userSnap.exists()) {
                  const userData = userSnap.data();
                  console.log('✅ useChatData - User data fetched from users collection:', userData);
                  
                  enhancedData = {
                    ...data,
                    lawyerName: data.lawyerName || userData.full_name || userData.fullName || userData.name || userData.displayName || 'Lawyer',
                    lawyerProfilePic: data.lawyerProfilePic || data.lawyerPhoto || userData.profile_picture || userData.profilePic || userData.photoURL || '/default-lawyer.png',
                    specialization: data.specialization || userData.specialization || userData.practiceAreas?.join(', ') || "General Law"
                  };
                } else {
                  console.log('⚠️ useChatData - Lawyer not found in either collection, using fallback data');
                  // Set fallback data
                  enhancedData = {
                    ...data,
                    lawyerName: data.lawyerName || 'Lawyer',
                    lawyerProfilePic: data.lawyerProfilePic || data.lawyerPhoto || '/default-lawyer.png',
                    specialization: data.specialization || "General Law"
                  };
                }
              }
            } catch (lawyerError) {
              console.warn('⚠️ useChatData - Could not fetch lawyer details:', lawyerError);
              // Set fallback data on error
              enhancedData = {
                ...data,
                lawyerName: data.lawyerName || 'Lawyer',
                lawyerProfilePic: data.lawyerProfilePic || data.lawyerPhoto || '/default-lawyer.png',
                specialization: data.specialization || "General Law"
              };
            }
          }
          
          console.log('🔍 useChatData - Enhanced lawyer details:', {
            lawyerName: enhancedData.lawyerName,
            lawyerProfilePic: enhancedData.lawyerProfilePic,
            specialization: enhancedData.specialization
          });
          
          setChatData(enhancedData);
        } else {
          console.log('❌ useChatData - Chat not found for ID:', chatId);
          setError("Chat not found");
        }
        setLoading(false);
      } catch (err) {
        console.error("❌ useChatData - Error fetching chat data:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchChat();
  }, [chatId]);

  return { chatData, loading, error, setChatData };
};
